import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

// ─── Question options ─────────────────────────────────────────────────────────

const YEARS_OPTIONS = ['Menos de 1 año', '1 - 2 años', '2 - 5 años', '5+ años'];
const ROLE_OPTIONS = [
  'Frontend Developer',
  'Backend Developer',
  'Full Stack Developer',
  'Mobile Developer',
  'Data Engineer',
  'Data Scientist',
  'ML / AI Engineer',
  'MLOps Engineer',
  'DevOps / Cloud',
  'Platform Engineer',
  'Security Engineer',
  'QA Engineer',
  'SRE',
  'Otro',
];
const TECH_OPTIONS = [
  'JavaScript',
  'TypeScript',
  'Python',
  'Java',
  'Go',
  'C#',
  'PHP',
  'Ruby',
  'Rust',
  'SQL',
  'Node.js',
  'React',
  'Next.js',
  'NestJS',
  'Django',
  'FastAPI',
  'Spring Boot',
  'PostgreSQL',
  'MongoDB',
  'Redis',
  'Docker',
  'Kubernetes',
  'Terraform',
  'AWS',
  'Lambda',
  'EC2',
  'S3',
  'RDS',
  'CloudWatch',
  'ECS',
  'EKS',
  'Azure',
  'GCP',
  'OpenAI API',
  'LangChain',
  'RAG',
  'Vector Databases',
  'LLMs',
  'Machine Learning',
  'Deep Learning',
  'PyTorch',
  'TensorFlow',
  'Hugging Face',
  'Airflow',
  'Spark',
  'Otro',
];
const SEEKING_OPTIONS = ['Buscando activamente', 'Explorando opciones', 'Abierto a ofertas', 'No estoy buscando'];
const REGION_OPTIONS = ['USA / Canadá', 'Europa', 'Latam / Remoto Latam', 'España', 'Cualquier región'];

const INTERVIEWS_OPTIONS = ['0', '1 - 3', '4 - 10', 'Más de 10'];
const OFFERS_OPTIONS = ['0', '1 - 2', '3 - 5', 'Más de 5'];
const CHALLENGE_OPTIONS = ['Conseguir entrevistas', 'Pasar las entrevistas técnicas', 'Negociación salarial', 'Barrera del idioma', 'Portafolio / experiencia insuficiente', 'Otro'];
const COMPANY_OPTIONS = ['Startups', 'Empresas medianas', 'Big Tech / FAANG', 'Remote-first', 'Cualquiera'];

// ─── Reusable option button ───────────────────────────────────────────────────

function OptionButton({ label, selected, onClick }: { label: string; selected: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-4 py-2.5 rounded-xl text-sm font-medium border transition-all text-left ${
        selected
          ? 'bg-primary text-on-primary border-primary'
          : 'bg-surface-container text-on-surface border-outline-variant/30 hover:border-primary/50 hover:bg-surface-container-high'
      }`}
    >
      {label}
    </button>
  );
}

function MultiOptionButton({ label, selected, onClick }: { label: string; selected: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-4 py-2.5 rounded-xl text-sm font-medium border transition-all flex items-center gap-2 ${
        selected
          ? 'bg-primary/10 text-primary border-primary'
          : 'bg-surface-container text-on-surface border-outline-variant/30 hover:border-primary/50 hover:bg-surface-container-high'
      }`}
    >
      <div className={`w-4 h-4 rounded flex items-center justify-center shrink-0 border transition-all ${selected ? 'bg-primary border-primary' : 'border-outline-variant'}`}>
        {selected && <span className="material-symbols-outlined text-on-primary text-[12px]" style={{ fontVariationSettings: "'FILL' 1" }}>check</span>}
      </div>
      {label}
    </button>
  );
}

// ─── Step indicator ───────────────────────────────────────────────────────────

function StepIndicator({ step, total }: { step: number; total: number }) {
  return (
    <div className="flex items-center gap-2">
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          className={`h-1.5 rounded-full transition-all duration-300 ${i < step ? 'bg-primary' : 'bg-surface-container-high'} ${i === step - 1 ? 'w-8' : 'w-4'}`}
        />
      ))}
    </div>
  );
}

// ─── Screen 1: Professional background ───────────────────────────────────────

interface Screen1Data {
  yearsOfExperience: string;
  currentRole: string;
  techStack: string[];
  jobSeekingStatus: string;
  targetRegion: string;
}

function Screen1({ data, onChange }: { data: Screen1Data; onChange: (d: Partial<Screen1Data>) => void }) {
  const toggleTech = (t: string) => {
    const next = data.techStack.includes(t)
      ? data.techStack.filter((x) => x !== t)
      : [...data.techStack, t];
    onChange({ techStack: next });
  };

  return (
    <div className="space-y-8">
      <div>
        <p className="text-sm font-semibold text-on-surface mb-3">¿Cuánto tiempo llevas en la industria tech?</p>
        <div className="flex flex-wrap gap-2">
          {YEARS_OPTIONS.map((o) => (
            <OptionButton key={o} label={o} selected={data.yearsOfExperience === o} onClick={() => onChange({ yearsOfExperience: o })} />
          ))}
        </div>
      </div>

      <div>
        <p className="text-sm font-semibold text-on-surface mb-3">¿Cuál es tu rol actual o más reciente?</p>
        <div className="flex flex-wrap gap-2">
          {ROLE_OPTIONS.map((o) => (
            <OptionButton key={o} label={o} selected={data.currentRole === o} onClick={() => onChange({ currentRole: o })} />
          ))}
        </div>
      </div>

      <div>
        <p className="text-sm font-semibold text-on-surface mb-3">¿En qué tecnologías tienes más experiencia? <span className="text-on-surface-variant font-normal">(elige las que quieras)</span></p>
        <div className="flex flex-wrap gap-2">
          {TECH_OPTIONS.map((o) => (
            <MultiOptionButton key={o} label={o} selected={data.techStack.includes(o)} onClick={() => toggleTech(o)} />
          ))}
        </div>
      </div>

      <div>
        <p className="text-sm font-semibold text-on-surface mb-3">¿Cuál es tu situación actual?</p>
        <div className="flex flex-wrap gap-2">
          {SEEKING_OPTIONS.map((o) => (
            <OptionButton key={o} label={o} selected={data.jobSeekingStatus === o} onClick={() => onChange({ jobSeekingStatus: o })} />
          ))}
        </div>
      </div>

      <div>
        <p className="text-sm font-semibold text-on-surface mb-3">¿En qué región buscas trabajo?</p>
        <div className="flex flex-wrap gap-2">
          {REGION_OPTIONS.map((o) => (
            <OptionButton key={o} label={o} selected={data.targetRegion === o} onClick={() => onChange({ targetRegion: o })} />
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Screen 2: Job search history ────────────────────────────────────────────

interface Screen2Data {
  interviewsLast6Months: string;
  offersLastYear: string;
  biggestChallenge: string;
  targetCompanyType: string;
}

function Screen2({ data, onChange }: { data: Screen2Data; onChange: (d: Partial<Screen2Data>) => void }) {
  return (
    <div className="space-y-8">
      <div>
        <p className="text-sm font-semibold text-on-surface mb-3">¿Cuántas entrevistas técnicas tuviste en los últimos 6 meses?</p>
        <div className="flex flex-wrap gap-2">
          {INTERVIEWS_OPTIONS.map((o) => (
            <OptionButton key={o} label={o} selected={data.interviewsLast6Months === o} onClick={() => onChange({ interviewsLast6Months: o })} />
          ))}
        </div>
      </div>

      <div>
        <p className="text-sm font-semibold text-on-surface mb-3">¿Cuántas ofertas laborales recibiste en el último año?</p>
        <div className="flex flex-wrap gap-2">
          {OFFERS_OPTIONS.map((o) => (
            <OptionButton key={o} label={o} selected={data.offersLastYear === o} onClick={() => onChange({ offersLastYear: o })} />
          ))}
        </div>
      </div>

      <div>
        <p className="text-sm font-semibold text-on-surface mb-3">¿Cuál es tu mayor obstáculo en la búsqueda laboral?</p>
        <div className="flex flex-wrap gap-2">
          {CHALLENGE_OPTIONS.map((o) => (
            <OptionButton key={o} label={o} selected={data.biggestChallenge === o} onClick={() => onChange({ biggestChallenge: o })} />
          ))}
        </div>
      </div>

      <div>
        <p className="text-sm font-semibold text-on-surface mb-3">¿Qué tipo de empresas te interesan?</p>
        <div className="flex flex-wrap gap-2">
          {COMPANY_OPTIONS.map((o) => (
            <OptionButton key={o} label={o} selected={data.targetCompanyType === o} onClick={() => onChange({ targetCompanyType: o })} />
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

const TITLES = [
  { title: 'Tu perfil profesional', subtitle: 'Cuéntanos sobre tu experiencia para personalizar tu camino.' },
  { title: 'Tu búsqueda laboral', subtitle: 'Queremos entender tu situación actual para ayudarte mejor.' },
];

export default function Onboarding() {
  const navigate = useNavigate();
  const { user, loading, refreshUser } = useAuth();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'ADMIN' || user.onboardingCompleted) {
    return <Navigate to={user.status === 'PENDING' ? '/pending' : '/dashboard'} replace />;
  }

  const [screen1, setScreen1] = useState<Screen1Data>({
    yearsOfExperience: '',
    currentRole: '',
    techStack: [],
    jobSeekingStatus: '',
    targetRegion: '',
  });

  const [screen2, setScreen2] = useState<Screen2Data>({
    interviewsLast6Months: '',
    offersLastYear: '',
    biggestChallenge: '',
    targetCompanyType: '',
  });

  const screen1Complete =
    !!screen1.yearsOfExperience &&
    !!screen1.currentRole &&
    screen1.techStack.length > 0 &&
    !!screen1.jobSeekingStatus &&
    !!screen1.targetRegion;

  const screen2Complete =
    !!screen2.interviewsLast6Months &&
    !!screen2.offersLastYear &&
    !!screen2.biggestChallenge &&
    !!screen2.targetCompanyType;

  const handleNext = () => setStep(2);

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await api.post('/users/onboarding', { ...screen1, ...screen2 });
      await refreshUser();
      navigate('/pending');
    } catch (e: any) {
      setError(e.message ?? 'Ocurrió un error. Intenta de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  const { title, subtitle } = TITLES[step - 1];

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        {/* Header */}
        <div className="mb-8 space-y-1">
          <StepIndicator step={step} total={2} />
          <p className="text-xs text-on-surface-variant mt-3">Paso {step} de 2</p>
          <h1 className="text-2xl font-bold text-on-surface">{title}</h1>
          <p className="text-sm text-on-surface-variant">{subtitle}</p>
        </div>

        {/* Card */}
        <div className="bg-surface-container-low rounded-2xl p-8 border border-outline-variant/10 shadow-sm">
          {step === 1 ? (
            <Screen1 data={screen1} onChange={(d) => setScreen1((p) => ({ ...p, ...d }))} />
          ) : (
            <Screen2 data={screen2} onChange={(d) => setScreen2((p) => ({ ...p, ...d }))} />
          )}
        </div>

        {error && (
          <p className="mt-4 text-sm text-error text-center">{error}</p>
        )}

        {/* Footer nav */}
        <div className="mt-6 flex items-center justify-between">
          {step === 2 ? (
            <button
              type="button"
              onClick={() => setStep(1)}
              className="flex items-center gap-1 text-sm text-on-surface-variant hover:text-on-surface transition-colors"
            >
              <span className="material-symbols-outlined text-[18px]">arrow_back</span>
              Anterior
            </button>
          ) : (
            <div />
          )}

          {step === 1 ? (
            <button
              type="button"
              onClick={handleNext}
              disabled={!screen1Complete}
              className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-primary text-on-primary text-sm font-semibold disabled:opacity-40 hover:bg-primary/90 transition-colors"
            >
              Siguiente
              <span className="material-symbols-outlined text-[18px]">arrow_forward</span>
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!screen2Complete || submitting}
              className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-primary text-on-primary text-sm font-semibold disabled:opacity-40 hover:bg-primary/90 transition-colors"
            >
              {submitting ? 'Enviando...' : 'Finalizar'}
              {!submitting && <span className="material-symbols-outlined text-[18px]">check</span>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
